import { jsPDF } from "jspdf"
import { getFile } from "features/dinamic"
import selloDorado from "resources/sello.png"
import { withSlash } from "pages/Alumno/cuenta/utils/formatters"
import templateDiplomaNinos from "resources/diploma_template_ninos.png"
import templateDiplomaJovenes from "resources/diploma_template_jovenes.png"
import templateDiplomaCiudadanos from "resources/diploma_template_ciudadanos.png"
import templateDiplomaDefault from "resources/diploma_template_default.png"
import { centuryGothicBold } from "./century-gothic-bold"
import { centuryGothic } from "./century-gothic"
// ============================================================================
// CONSTANTES DE POSICIONAMIENTO
// ============================================================================

const A4_WIDTH_PT = 595.28
const A4_HEIGHT_PT = 841.89

// Escala de imagen original (1413x2000) a A4
const IMG_WIDTH = 1413
const IMG_HEIGHT = 2000
const SCALE_X = A4_WIDTH_PT / IMG_WIDTH

// Centro REAL del área de contenido (excluyendo franja azul)
// Área de contenido: 229px - 1400px → centro = 814px
const CONTENT_CENTER_PX = 814
const CONTENT_CENTER_PT = CONTENT_CENTER_PX * SCALE_X // ~343pt

// Ancho disponible para texto (área blanca menos márgenes)
const CONTENT_WIDTH_PX = 1400 - 229 - 80 // Con margen interno
const CONTENT_WIDTH_PT = CONTENT_WIDTH_PX * SCALE_X

// Margen izquierdo del contenido (donde empieza el texto con viñetas)
const CONTENT_LEFT_PX = 310
const CONTENT_LEFT_PT = CONTENT_LEFT_PX * SCALE_X

// Posiciones Y
const NAME_Y_PT = 150           // Entre "CERTIFICA A:" y "Por haber concluido"
const DIPLOMA_NAME_Y_PT = 228 // Justo después de "Por haber concluido el Diplomado"
const COMPETENCIAS_Y_PT = 255  // Donde empiezan las competencias

// Badges (esquina inferior derecha)
const BADGES_Y_PT = 685
const BADGE_SIZE_PT = 85
const BADGE_GAP_PT = 15

// Footer
const FOOTER_Y_PT = 805
const FOOTER_Y_PT_2 = 820

// ============================================================================
// COMPETENCIAS PARA PLANTILLA DEFAULT
// ============================================================================

const COMPETENCIAS_DEFAULT = [
  "Capacidad para reconocer su identidad, fortalezas y valores, fortaleciendo la autoestima y el sentido de pertenencia a su comunidad.",
  "Desarrollo de habilidades de liderazgo, comunicación efectiva, trabajo en equipo y resolución pacífica de conflictos.",
  "Comprensión de derechos y responsabilidades ciudadanas, promoviendo la participación activa y consciente en la sociedad.",
  "Habilidad para identificar situaciones de riesgo y tomar decisiones responsables orientadas al bienestar personal y colectivo.",
  "Competencia para construir un proyecto de vida con metas claras a corto, mediano y largo plazo, asumiendo un rol activo como agente de cambio."
]

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

const resolveImageType = (fileName?: string): "JPEG" | "PNG" | "WEBP" => {
  const ext = fileName?.split(".").pop()?.toLowerCase()
  if (ext === "jpg" || ext === "jpeg") return "JPEG"
  if (ext === "webp") return "WEBP"
  return "PNG"
}

const fetchRemoteImage = async (fileUrl: string, fileName?: string) => {
  const response = await getFile({ fileUrl, fileName: fileName || "image" })
  if (!response?.blob) return null
  return blobToDataUrl(response.blob)
}

const fetchLocalImage = async (url: string) => {
  const response = await fetch(url)
  if (!response.ok) return null
  const blob = await response.blob()
  return blobToDataUrl(blob)
}

const fitImage = (doc: jsPDF, dataUrl: string, maxWidth: number, maxHeight: number) => {
  const props = doc.getImageProperties(dataUrl)
  const width = props.width || maxWidth
  const height = props.height || maxHeight
  const scale = Math.min(maxWidth / width, maxHeight / height, 1)
  return { width: width * scale, height: height * scale }
}

const normalizeDiplomaName = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")

// Nombres de diplomas normalizados para comparación
const DIPLOMA_NINOS = normalizeDiplomaName("Niños y Niñas Líderes en Acción")
const DIPLOMA_JOVENES = normalizeDiplomaName("Jóvenes Líderes en Acción")
const DIPLOMA_CIUDADANOS = normalizeDiplomaName("Ciudadanos Líderes en Acción")

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

export const buildDiplomaPdfBlob = async (data: any) => {
  const diplomaKey = normalizeDiplomaName(data?.nombreDiplomado)

  // Determinar qué plantilla usar
  const isDefaultTemplate =
    diplomaKey !== DIPLOMA_NINOS &&
    diplomaKey !== DIPLOMA_JOVENES &&
    diplomaKey !== DIPLOMA_CIUDADANOS

  const templateUrl =
    diplomaKey === DIPLOMA_NINOS
      ? templateDiplomaNinos
      : diplomaKey === DIPLOMA_JOVENES
        ? templateDiplomaJovenes
        : diplomaKey === DIPLOMA_CIUDADANOS
          ? templateDiplomaCiudadanos
          : templateDiplomaDefault

  // Crear documento PDF A4 portrait
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  })

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  // ============================================================================
  // 1. DIBUJAR PLANTILLA DE FONDO
  // ============================================================================
  const templateDataUrl = await fetchLocalImage(templateUrl)
  if (templateDataUrl) {
    doc.addImage(templateDataUrl, "PNG", 0, 0, pageWidth, pageHeight)
  }

  // ============================================================================
  // 2. DIBUJAR NOMBRE DEL ALUMNO
  // ============================================================================
  const fullName = String(data?.nombreCompleto || "ALUMNO").toUpperCase()

  const drawName = () => {

    let fontSize = 20
    let lines: string[] = []
    doc.addFileToVFS("CenturyGothic-Bold.ttf", centuryGothicBold)
    doc.addFont("CenturyGothic-Bold.ttf", "CenturyGothic", "bold")

    doc.setFont("CenturyGothic", "bold")
    doc.setTextColor(25, 25, 30)

    const maxWidth = CONTENT_WIDTH_PT - 20

    while (fontSize >= 14) {
      doc.setFontSize(fontSize)
      lines = doc.splitTextToSize(fullName, maxWidth) as string[]
      if (lines.length <= 2) break
      fontSize -= 1
    }

    if (lines.length > 2) {
      lines = [lines[0], lines.slice(1).join(" ")]
      fontSize = Math.max(12, fontSize - 2)
      doc.setFontSize(fontSize)
      const secondLineWords = lines[1].split(" ")
      if (secondLineWords.length > 6) {
        lines[1] = secondLineWords.slice(0, 6).join(" ") + "..."
      }
    }

    // Calcular posición Y para centrar verticalmente el bloque de texto
    const lineHeight = fontSize * 1.25
    const blockHeight = lines.length * lineHeight
    const startY = NAME_Y_PT - (blockHeight / 2) + lineHeight

    // Dibujar cada línea centrada
    lines.forEach((line, index) => {
      doc.text(line, CONTENT_CENTER_PT, startY + (index * lineHeight), { align: "center" })
    })
  }

  drawName()

  // ============================================================================
  // 3. CONTENIDO ESPECÍFICO PARA PLANTILLA DEFAULT
  // ============================================================================
  if (isDefaultTemplate) {
    // 3a. Nombre del diplomado (en dorado)
    const diplomadoName = String(data?.nombreDiplomado || "")
    if (diplomadoName) {
      doc.addFileToVFS("CenturyGothic.ttf", centuryGothic)
      doc.addFont("CenturyGothic.ttf", "CenturyGothic", "normal")
      doc.setFontSize(14)
      doc.setTextColor(184, 134, 11) // Dorado
      doc.text(diplomadoName, CONTENT_CENTER_PT, DIPLOMA_NAME_Y_PT, { align: "center" })
    }

    // 3b. Título "Y con ello haber desarrollado las competencias:"
    doc.addFileToVFS("CenturyGothic.ttf", centuryGothic)
    doc.addFont("CenturyGothic.ttf", "CenturyGothic", "normal")
    doc.setFont("CenturyGothic", "normal")
    doc.setFontSize(11)
    doc.setTextColor(30, 30, 35)
    // 3c. Lista de competencias con viñetas
    doc.addFileToVFS("CenturyGothic.ttf", centuryGothic)
    doc.addFont("CenturyGothic.ttf", "CenturyGothic", "normal")
    doc.setFont("CenturyGothic", "normal")
    doc.setFontSize(12)
    doc.setTextColor(50, 50, 55)

    const bulletX = CONTENT_LEFT_PT
    const textX = bulletX + 12
    const textMaxWidth = CONTENT_WIDTH_PT - 70
    let currentY = COMPETENCIAS_Y_PT + 20

    COMPETENCIAS_DEFAULT.forEach((competencia) => {
      // Dibujar viñeta (círculo pequeño)
      doc.setFillColor(50, 50, 55)
      doc.circle(bulletX + 3, currentY - 3, 2, "F")

      // Texto de la competencia
      const lines = doc.splitTextToSize(competencia, textMaxWidth) as string[]
      lines.forEach((line, lineIndex) => {
        doc.text(line, textX, currentY + (lineIndex * 11))
      })

      // Mover Y para la siguiente competencia
      currentY += lines.length * 11 + 8
    })
  }

  // ============================================================================
  // 4. DIBUJAR INSIGNIAS Y SELLO DORADO
  // ============================================================================
  const insigniaBasepath = withSlash(data?.basepathInsignia)
  const insigniaFile = data?.imagenInsignia
  const showSelloDorado = Boolean(data?.conSelloDorado)

  const badgeCount = (insigniaFile ? 1 : 0) + (showSelloDorado ? 1 : 0)
  
  if (badgeCount > 0) {
    const totalBadgesWidth = badgeCount * BADGE_SIZE_PT + (badgeCount > 1 ? BADGE_GAP_PT : 0)
    const badgesStartX = CONTENT_CENTER_PT - (totalBadgesWidth / 2)
    let currentBadgeX = badgesStartX

    const drawBadge = async (
      dataUrl: string,
      x: number,
      y: number,
      imageType: "PNG" | "JPEG" | "WEBP"
    ) => {
      const fitted = fitImage(doc, dataUrl, BADGE_SIZE_PT - 10, BADGE_SIZE_PT - 10)

      // Sombra
      doc.setFillColor(200, 200, 205)
      doc.roundedRect(x + 3, y + 3, BADGE_SIZE_PT, BADGE_SIZE_PT, 8, 8, "F")

      // Fondo blanco
      doc.setFillColor(255, 255, 255)
      doc.roundedRect(x, y, BADGE_SIZE_PT, BADGE_SIZE_PT, 8, 8, "F")

      // Borde sutil
      doc.setDrawColor(220, 220, 225)
      doc.setLineWidth(0.5)
      doc.roundedRect(x, y, BADGE_SIZE_PT, BADGE_SIZE_PT, 8, 8, "S")

      // Imagen centrada
      doc.addImage(
        dataUrl,
        imageType,
        x + (BADGE_SIZE_PT - fitted.width) / 2,
        y + (BADGE_SIZE_PT - fitted.height) / 2,
        fitted.width,
        fitted.height
      )
    }

    // Dibujar insignia si existe
    if (insigniaBasepath && insigniaFile) {
      const insigniaUrl = `${insigniaBasepath}${insigniaFile}`
      const insigniaDataUrl = await fetchRemoteImage(insigniaUrl, insigniaFile)
      if (insigniaDataUrl) {
        await drawBadge(insigniaDataUrl, currentBadgeX, BADGES_Y_PT, resolveImageType(insigniaFile))
        currentBadgeX += BADGE_SIZE_PT + BADGE_GAP_PT
      }
    }

    // Dibujar sello dorado si aplica
    if (showSelloDorado) {
      const selloDataUrl = await fetchLocalImage(selloDorado)
      if (selloDataUrl) {
        await drawBadge(selloDataUrl, currentBadgeX, BADGES_Y_PT, "PNG")
      }
    }
  }

  // ============================================================================
  // 5. DIBUJAR FOOTER (Carnet y CUI)
  // ============================================================================
  const carnet = data?.carnet ? String(data.carnet) : ""
  const cui = data?.cui ? String(data.cui) : ""
  const footerParts: string[] = []
  if (carnet) footerParts.push(`Carnet: ${carnet}`)
  if (cui) footerParts.push(`CUI: ${cui}`)

  if (footerParts.length) {
    doc.addFileToVFS("CenturyGothic.ttf", centuryGothic)
    doc.addFont("CenturyGothic.ttf", "CenturyGothic", "normal")
    doc.setFontSize(9)
    doc.setTextColor(90, 90, 95)
    doc.text(footerParts.join("  |  "), CONTENT_CENTER_PT, FOOTER_Y_PT, { align: "center" })
    
  }

  const emision = data?.fechaEmision ? String(data.fechaEmision) : ""
  const facilitador = data?.nombreAutorizador ? String(data.nombreAutorizador) : ""
  const footerParts2: string[] = []
  if (emision) footerParts2.push(`Fecha Emisión: ${emision}`)
  if (facilitador) footerParts2.push(`Facilitador: ${facilitador}`)
    doc.addFileToVFS("CenturyGothic.ttf", centuryGothic)
    doc.addFont("CenturyGothic.ttf", "CenturyGothic", "normal")
    doc.setFontSize(8)
    doc.setTextColor(90, 90, 95)
    doc.text(footerParts2.join("  |  "), CONTENT_CENTER_PT, FOOTER_Y_PT_2, { align: "center" })

  return doc.output("blob")
}
